import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import Container from './Container';
import LanguageSwitcher from '@/components/nav/LanguageSwitcher';
import MegaMenu from '@/components/nav/MegaMenu';
import MobileMenu from '@/components/nav/MobileMenu';
import SearchBox from '@/components/nav/SearchBox';
import ThemeToggle from '@/components/nav/ThemeToggle';
import ComfortToggle from '@/components/nav/ComfortToggle';
// 2026-05-26 — Patch v8 personas : chip qui affiche la persona active de
// l'utilisateur (auto-hide si pas pickée). Visible desktop uniquement
// (md+) pour ne pas saturer le header mobile.
import PersonaBadge from '@/components/personas/PersonaBadge';
import { SUPPORTED_LANGS, DEFAULT_LANG } from '@/i18n';
import { pathFor } from '@/data/routes';
import { buildSearchIndex } from '@/utils/articles';

export default function Header() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    let frame = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setScrolled(window.scrollY > 8));
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  const lang = SUPPORTED_LANGS.includes(i18n.language) ? i18n.language : DEFAULT_LANG;
  const homeHref = pathFor('home', lang);
  // Memoize the search index — Header re-renders on every route change
  // (scroll + searchOpen state), so without memo we were rebuilding the
  // 58-entry index on every nav. ~1ms but free to skip.
  const searchIndex = useMemo(() => buildSearchIndex(lang), [lang]);
  // Memo obligatoire ici aussi : ~390 paires par langue, et une nouvelle
  // référence de tableau ferait reconstruire l'index Fuse a chaque rendu.

  return (
    <>
      <a href="#main" className="skip-link">
        {t('common:skipToContent')}
      </a>

      {/* 2026-06 — Header clair (fond papier) : pose le logo OFFICIEL couleur
          sur son fond naturel, sans pastille. Opaque en permanence (masthead
          éditorial), filet hairline + ombre douce au scroll. Sombre en dark
          mode ; nav en encre (clair) / crème (dark). */}
      <header
        className={`fixed inset-x-0 top-0 z-30 border-b border-hairline bg-cream-50 transition-shadow duration-300 dark:border-white/10 dark:bg-zinc-950 ${
          scrolled
            ? 'shadow-[0_2px_24px_rgba(35,32,25,0.10)] dark:shadow-[0_2px_24px_rgba(0,0,0,0.5)]'
            : ''
        }`}
        style={{ height: 80 }}
      >
        <Container size="wide" className="flex h-20 items-center justify-between">
          {/* Logo Inside Panama — logo OFFICIEL couleur (drapeau + butte +
              skyline) posé directement sur le header papier (pas de pastille)
              + wordmark DM Serif romain : « Inside » encre, « Panama » rouille
              (la vraie charte). Crème en dark mode. */}
          <Link
            to={homeHref}
            aria-label={t('common:siteName')}
            className="flex items-center gap-2 transition-opacity hover:opacity-80"
          >
            <img
              src="/logo-symbol.png"
              alt=""
              width="40"
              height="40"
              className="h-10 w-10 shrink-0"
            />
            <span className="font-serif text-xl tracking-tight text-navy-900 dark:text-cream-50">
              Inside <span className="text-bordeaux-700 dark:text-cream-200">Panama</span>
            </span>
          </Link>

          <MegaMenu lang={lang} scrolled={false} />

          <div className="flex items-center gap-1.5">
            {/* Persona badge : auto-hide si aucune persona pickée — pas de
                clutter pour les nouveaux visiteurs (Patch v8 / Playbook v1.0).
                Visible à partir de xl et non plus de md : MESURÉ à 1024 px, la
                barre réclamait 1 103 px pour 1 009 px disponibles et poussait le
                sélecteur de langue hors de l'écran. Le badge (130 px) est ce
                qu'on peut retirer sans rien perdre — il n'apparaît que si un
                profil est choisi, et l'onglet Profil du méga-menu montre déjà
                lequel. Le débordement préexistait (~22 px) ; l'onglet Profil
                l'aurait porté à 111 px. */}
            <PersonaBadge className="hidden xl:inline-flex" />

            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              aria-label={t('navigation:openSearch')}
              className="rounded-btn p-2 text-navy-900 transition-colors hover:bg-navy-900/5 dark:text-cream-50 dark:hover:bg-white/10"
            >
              <Search size={18} aria-hidden="true" />
            </button>

            {/* Comfort mode (accessibility / pensionado) — typo +18 %, contraste
                renforcé, animations off. Inspiré du mode senior d'Uber. */}
            <ComfortToggle className="hidden sm:inline-flex" />

            {/* Theme toggle — hidden on small screens to save space.
                Mobile users get the toggle inside MobileMenu drawer. */}
            <ThemeToggle className="hidden sm:inline-flex" />

            <LanguageSwitcher variant="light" />

            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              aria-label={t('navigation:openMenu')}
              className="rounded-btn p-2 text-navy-900 transition-colors hover:bg-navy-900/5 dark:text-cream-50 dark:hover:bg-white/10 lg:hidden"
            >
              <Menu size={20} aria-hidden="true" />
            </button>
          </div>
        </Container>
      </header>

      <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} lang={lang} />
      <SearchBox open={searchOpen} onClose={() => setSearchOpen(false)} documents={searchIndex} />
    </>
  );
}
